import React from 'react';

import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';

import {useTenantInfo} from 'modules/tenant/hooks';
import {formatAxiosErrorOrThrow} from 'shared/utils/error';
import {createUserFromSingleUseSignup} from 'modules/user/api/user';
import {useSingleUseSignupSummaryFromUrl} from 'modules/single-use-signup/hooks/useSingleUseSignupSummaryFromUrl';
import {ICreateUserForm} from 'users/pages/CreateUser/CreateUser';

import {FullScreenModal} from 'shared/components/FullScreenModal/FullScreenModal';
import {FullScreenModalTitle} from 'shared/components/FullScreenModal/FullScreenModalTitle';
import {LoadingError} from 'shared/components/loading/LoadingError/LoadingError';
import {LoadingSpinner} from 'shared/components/loading/LoadingSpinner/LoadingSpinner';
import { EmailVerification } from 'shared/pages/Signup/EmailVerification';
import { TeacherSignupForm } from 'shared/pages/Signup/TeacherSignup/TeacherSignupForm';

import '../styles.scss';

export const TeacherPaidSignup = () => {
    const {t} = useTranslation();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [emailConfirmation, setEmailConfirmation] = React.useState<string | undefined>();

    const {tenantInfo} = useTenantInfo();
    const {singleUseSignupSummary, loading, error} = useSingleUseSignupSummaryFromUrl(tenantInfo.id);

    const navigate = useNavigate();

    const onSubmit = ({role, ...userCreate}: ICreateUserForm) => {
        if (isSubmitting || !singleUseSignupSummary || !emailConfirmation) {
            return;
        }
        setIsSubmitting(true);
        (async () => {
            try {
                await createUserFromSingleUseSignup(
                    tenantInfo.id,
                    singleUseSignupSummary.id,
                    {
                        roles: [role],
                        ...userCreate,
                    },
                    emailConfirmation,
                );
                // redirect to login page
                navigate('/login');
                toast.success(t('signup.success', 'Signup successful! Please login'));
            } catch (e) {
                toast.error(formatAxiosErrorOrThrow(t(
                    'signup.submit.error', 'There was an error',
                ), e));
            } finally {
                setIsSubmitting(false);
            }
        })();
    };

    if (error) {
        return (
            <LoadingError />
        );
    } else if (loading) {
        return (
            <LoadingSpinner />
        );
    } else {
        return (
            <FullScreenModal close={false} size="md">
                <FullScreenModalTitle className="mb-4">
                    {tenantInfo.name === 'Demo' || 'test' ? (
                        t('common.teacherSignup.title', 'Sign up to Young Eyes as a teacher')
                    ) : (
                        t('common.teacherSignup.title', 'Sign up to Young Eyes {{tenantName}} as a teacher', {tenantName: tenantInfo.name})
                    )}
                </FullScreenModalTitle>
                {!emailConfirmation && singleUseSignupSummary ? (
                    <EmailVerification
                        setEmail={setEmailConfirmation}
                        singleUseSignupId={singleUseSignupSummary.id}
                        tenantId={tenantInfo.id}
                    />
                ) : (
                    <TeacherSignupForm
                        onGoBack={() => navigate(-1)}
                        isSubmitting={isSubmitting}
                        onSubmit={onSubmit}
                    />
                )}
            </FullScreenModal>
        );
    }
};